/**
 * Mutable store for planner vs off-route flow ids so Axios interceptors
 * (outside React) can read and update correlation state.
 * React subscribes via useSyncExternalStore.
 */

import { useSyncExternalStore } from "react";
import type { FlowCorrelationScope } from "./flowCorrelationScope";

type Listener = () => void;

const listeners = new Set<Listener>();

let plannerItineraryFlowId: string | null = null;

let offRouteRecalculationFlowId: string | null = null;

const emit = (): void => {
    for ( const listener of listeners ) {
        listener();
    }
};

export interface CorrelationFlowSnapshot {
    "plannerItineraryFlowId":      string | null;
    "offRouteRecalculationFlowId": string | null;
}

let currentSnapshot: CorrelationFlowSnapshot = {
    "plannerItineraryFlowId":      null,
    "offRouteRecalculationFlowId": null,
};

const refreshSnapshot = (): void => {
    currentSnapshot = {
        plannerItineraryFlowId,
        offRouteRecalculationFlowId,
    };
};

export const getCorrelationFlowSnapshot = (): CorrelationFlowSnapshot => {
    return currentSnapshot;
};

export const getCorrelationFlowServerSnapshot = (): CorrelationFlowSnapshot => ( {
    "plannerItineraryFlowId":      null,
    "offRouteRecalculationFlowId": null,
} );

export const subscribeCorrelationFlowStore = ( listener: Listener ):  () => void  => {
    listeners.add( listener );

    return (): void => {
        listeners.delete( listener );
    };
};

export const setPlannerItineraryFlowId = ( id: string | null ): void => {
    if ( plannerItineraryFlowId === id ) {
        return;
    }
    plannerItineraryFlowId = id;
    refreshSnapshot();
    emit();
};

export const setOffRouteRecalculationFlowId = ( id: string | null ): void => {
    if ( offRouteRecalculationFlowId === id ) {
        return;
    }
    offRouteRecalculationFlowId = id;
    refreshSnapshot();
    emit();
};

/**
 * Returns the existing off-route flow id or creates one and stores it.
 */
export const ensureOffRouteRecalculationFlowId = (): string => {
    if ( offRouteRecalculationFlowId !== null && offRouteRecalculationFlowId.length > 0 ) {
        return offRouteRecalculationFlowId;
    }
    const id = crypto.randomUUID();
    offRouteRecalculationFlowId = id;
    refreshSnapshot();
    emit();

    return id;
};

export const getPlannerItineraryFlowIdForRequests = (): string | null => {
    return plannerItineraryFlowId;
};

export const getOffRouteRecalculationFlowIdForRequests = (): string | null => {
    return offRouteRecalculationFlowId;
};

/**
 * Updates the active flow id for the given scope when the backend echoes headers.
 */
export const applyFlowIdFromResponse = ( scope: FlowCorrelationScope, flowIdFromHeader: string ): void => {
    if ( scope === "planner" ) {
        if ( plannerItineraryFlowId === flowIdFromHeader ) {
            return;
        }
        plannerItineraryFlowId = flowIdFromHeader;
    } else {
        if ( offRouteRecalculationFlowId === flowIdFromHeader ) {
            return;
        }
        offRouteRecalculationFlowId = flowIdFromHeader;
    }
    refreshSnapshot();
    emit();
};

/**
 * Starts a new planner search flow and clears the off-route flow (new itinerary from planner).
 */
export const beginNewPlannerItineraryFlow = (): string => {
    const id = crypto.randomUUID();
    plannerItineraryFlowId = id;
    offRouteRecalculationFlowId = null;
    refreshSnapshot();
    emit();

    return id;
};

/**
 * Subscribes React components to planner / off-route flow id changes.
 */
export const useCorrelationFlowSnapshot = (): CorrelationFlowSnapshot => {
    return useSyncExternalStore(
        subscribeCorrelationFlowStore,
        getCorrelationFlowSnapshot,
        getCorrelationFlowServerSnapshot,
    );
};
